import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { useGuardedMutation } from "model/useGuardedMutation";

export type ActionEventEvidence = {
  guid: string;
  actionSubmissionGuid: string;
  farmEventGuid: string;
  creationDate: string;
};

const baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v2/actionEventEvidence`;

function authHeaders() {
  return {
    Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
    "Content-Type": "application/json",
  };
}

async function fetchActionEventEvidence(
  actionSubmissionGuid: string
): Promise<ActionEventEvidence[]> {
  const res = await fetch(
    `${baseUrl}?actionSubmissionGuid=${encodeURIComponent(
      actionSubmissionGuid
    )}`,
    { headers: authHeaders() }
  );
  if (!res.ok) return [];
  return (await res.json()) as ActionEventEvidence[];
}

export function useActionEventEvidence(
  actionSubmissionGuid: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.actionEventEvidence.bySubmission(
      actionSubmissionGuid ?? ""
    ),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => fetchActionEventEvidence(actionSubmissionGuid!),
    enabled: !!actionSubmissionGuid,
  });
}

export function useShareEventAsEvidence() {
  const queryClient = useQueryClient();
  return useGuardedMutation({
    mutationFn: async ({
      actionSubmissionGuid,
      farmEventGuid,
    }: {
      actionSubmissionGuid: string;
      farmEventGuid: string;
    }) => {
      const res = await fetch(baseUrl, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ actionSubmissionGuid, farmEventGuid }),
      });
      if (!res.ok) throw new Error("Failed to share event as evidence");
      return (await res.json()) as { guid: string };
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.actionEventEvidence.bySubmission(
          variables.actionSubmissionGuid
        ),
      });
    },
  });
}

export function useUnshareEventAsEvidence() {
  const queryClient = useQueryClient();
  return useGuardedMutation({
    mutationFn: async ({
      guid,
      actionSubmissionGuid,
    }: {
      guid: string;
      actionSubmissionGuid: string;
    }) => {
      const res = await fetch(`${baseUrl}/${guid}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to unshare event");
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.actionEventEvidence.bySubmission(
          variables.actionSubmissionGuid
        ),
      });
      // Also invalidate action evidence since files were cascade-deleted
      await queryClient.invalidateQueries({
        queryKey: queryKeys.actionEvidence.bySubmission(
          variables.actionSubmissionGuid
        ),
      });
    },
  });
}
